// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Display team list only
// 2. Separation of Concerns - UI separated from data fetching
// 3. Modular Architecture - Reusable list component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';

import teamManagementService from '../../services/teamManagementService';
import ConfirmDialog from '../common/ConfirmDialog';
import AlertDialog from '../common/AlertDialog';

const TeamList = ({ teams, onEdit, onManageMembers, onDelete, onReactivate, onRefresh, showInactive }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, team: null });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info'
  });

  const [filterType, setFilterType] = useState('all'); // all, active, inactive
  const [sortBy, setSortBy] = useState('name'); // name, member_count, qa_criteria_count, created_at
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc

  // Filter and sort teams
  const filteredAndSortedTeams = useMemo(() => {
    let filtered = teams.filter(team => {
      // Search filter
      const matchesSearch = 
        team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (team.code && team.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (team.description && team.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      if (!matchesSearch) return false;
      
      // Status filter
      if (filterType === 'active') return team.is_active;
      if (filterType === 'inactive') return !team.is_active;
      return true; // 'all'
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'member_count':
          aVal = a.member_count || 0;
          bVal = b.member_count || 0;
          break;
        case 'qa_criteria_count':
          aVal = a.qa_criteria_count || 0;
          bVal = b.qa_criteria_count || 0;
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [teams, searchTerm, filterType, sortBy, sortOrder]);

  const handleDeleteClick = (team) => {
    setConfirmDialog({ open: true, team });
  };

  const handleDeleteConfirm = async () => {
    const { team } = confirmDialog;
    try {
      await teamManagementService.deleteTeam(team.id);
      setConfirmDialog({ open: false, team: null });
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Team "${team.name}" deleted successfully!`,
        severity: 'success'
      });
      onDelete();
    } catch (err) {
      console.error('Failed to delete team:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to delete team',
        severity: 'error'
      });
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDialog({ open: false, team: null });
  };

  const handleReactivateClick = (team) => {
    setConfirmDialog({ 
      open: true, 
      team,
      action: 'reactivate',
      title: 'Reactivate Team',
      message: `Are you sure you want to reactivate team "${team.name}"?`
    });
  };

  const handleReactivateConfirm = async () => {
    const { team } = confirmDialog;
    try {
      await onReactivate(team);
      setConfirmDialog({ open: false, team: null });
    } catch (err) {
      console.error('Failed to reactivate team:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to reactivate team',
        severity: 'error'
      });
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      {/* Search and Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search teams by name, code, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterType}
            label="Status"
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="member_count">Members</MenuItem>
            <MenuItem value="qa_criteria_count">QA Criteria</MenuItem>
            <MenuItem value="created_at">Created Date</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Order</InputLabel>
          <Select
            value={sortOrder}
            label="Order"
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Teams Table */}
      {filteredAndSortedTeams.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No teams match your search' : 'No teams available'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try a different search term' : 'Create your first team to get started'}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Team Name</strong></TableCell>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Members</strong></TableCell>
                <TableCell><strong>QA Criteria</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedTeams.map((team) => (
                <TableRow key={team.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {team.name}
                      </Typography>
                      {team.description && (
                        <Typography variant="caption" color="text.secondary">
                          {team.description.length > 50
                            ? `${team.description.substring(0, 50)}...`
                            : team.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={team.code}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={team.team_type || 'operational'}
                      size="small"
                      color="default"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<GroupIcon />}
                      label={team.member_count || 0}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={team.qa_criteria_count || 0}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={team.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={team.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {team.is_active ? (
                        <>
                          <Tooltip title="Manage Members">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onManageMembers(team)}
                            >
                              <GroupIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Team">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => onEdit(team)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Team">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(team)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Reactivate Team">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReactivateClick(team)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={handleDeleteCancel}
        onConfirm={confirmDialog.action === 'reactivate' ? handleReactivateConfirm : handleDeleteConfirm}
        title={confirmDialog.title || "Delete Team"}
        message={confirmDialog.message || `Are you sure you want to delete team "${confirmDialog.team?.name}"? This will remove all team members and may affect QA Criteria access.`}
        confirmText={confirmDialog.action === 'reactivate' ? "Reactivate" : "Delete"}
        cancelText="Cancel"
        severity={confirmDialog.action === 'reactivate' ? "info" : "warning"}
        confirmColor={confirmDialog.action === 'reactivate' ? "success" : "error"}
      />

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Paper>
  );
};

export default TeamList;
